/**
 * The OUTSIDE of a track: the ground around the map and the trees in it, plus the pieces of the surface
 * that are the same ground (the base grass, its tile, the scatter).
 *
 * It is its own module because the game needs exactly this much and no more: a track's own look is the
 * editor's picture, while the band around the map is drawn here, once per load. The painter imports from
 * this file for the grass it paints into that picture, so the two can never disagree about grass.
 */

/** How far past the map the outside is drawn: as far as a camera sitting ON the map's edge can see at the
 *  smallest zoom the game uses (half a viewport, ~1600 world units), plus a margin. The cars never leave
 *  the track — the barriers see to that — so that band is all the outside the game can ever show. */
const MARGIN = 1800;

/** How far inside the map the view has to be before the band can be left out of the frame (see
 *  _updateVisibility): a few frames of camera movement, so a stale check can never show a hole. */
const INVISIBLE_SLACK = 256;

/**
 * The scenery AROUND the track, painted once when the track is loaded and then left alone.
 *
 * One canvas and one image, nothing per frame: the cars cannot leave the track, so the camera can only
 * ever look at the map plus a fixed band around it. Streaming that band in tiles (what this used to do)
 * cost a 1024x1024 canvas and its upload to the GPU every time another corner came into view — tens of
 * milliseconds in the frame it appeared in, always in the same place on the track.
 *
 * The look is the painter's, procedural variation and trees beyond the map included, exactly what the
 * editor's preview draws. The band is a backyard, not the track: its pixels per world unit follow their
 * own ceilings (see OUTSIDE_MAX_PIXELS), well below the picture's, because what has to be sharp is the
 * picture.
 */
export default class TrackSurroundings {
  constructor(scene) {
    this.scene = scene;
    this.track = null;
    this.image = null;
    this.key = null;
    this.generation = 0;
    this._flat = false;           // what is on screen is the flat wash, not the painted band (see setFlat)
    this._pendingUpload = false;  // the band still owes the GPU one drawn frame (see _buildScenery)
    scene.events.once('shutdown', this.destroy, this);
  }

  /** Everything this owns, gone: the picture object and the texture behind it (see _buildScenery). */
  clear() {
    this.scene.events.off('prerender', this._updateVisibility, this);
    this._flat = false;
    this._pendingUpload = false;
    this.image?.destroy();
    this.image = null;
    if (this.key && this.scene.textures.exists(this.key)) this.scene.textures.remove(this.key);
    this.key = null;
    this.track = null;
    this.generation++;
  }

  setTrack(track, opts) {
    if (this.track === track) return;
    this.clear();
    this.track = track;
    if (track?.width > 0 && track?.height > 0) this._buildScenery(track, opts);
  }

  /**
   * The stand-in for the band when the "Scenery" Graphics option is off: one flat rectangle of the grass
   * colour. No canvas, no texture, no trees — a switch that turns the outside into a black void would be
   * useless, and this costs nothing (a fill, and it is not even drawn while the view is inside the map).
   */
  setFlat(track) {
    if (this._flat && this.track === track) return;
    this.clear();
    this._flat = true;
    this.track = track;
    if (!(track?.width > 0) || !(track?.height > 0)) return;
    const bounds = outsideBounds(track);
    this.image = this.scene.add.rectangle(bounds.x, bounds.y, bounds.width, bounds.height, GRASS_FILL)
      .setOrigin(0)
      .setDepth(-2);
    this.scene.events.on('prerender', this._updateVisibility, this);
    this._updateVisibility();
  }

  /**
   * Paints the band around the map into one canvas: the painter leaves the map itself transparent (the
   * track's own picture is drawn over it), so what lands here is grass and the trees outside the map.
   */
  _buildScenery(track, opts) {
    const bounds = outsideBounds(track);
    const scale = outsideBandScale(bounds, opts?.maxPixels, opts?.maxScale);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bounds.width * scale));
    canvas.height = Math.max(1, Math.round(bounds.height * scale));
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    ctx.translate(MARGIN, MARGIN); // the painter works in the track's own coordinates
    createTrackSurroundingsPainter(track, opts)(ctx, bounds);

    const key = `track_outside_${this.generation}`;
    this.scene.textures.addCanvas(key, canvas);
    this.key = key;
    // The canvas is smaller than the band when the ceilings scaled it down (see above), so the image is
    // stretched to the band it stands for — the same thing the track's own picture does.
    this.image = this.scene.add.image(-MARGIN, -MARGIN, key).setOrigin(0).setDepth(-2);
    this.image.setDisplaySize(bounds.width, bounds.height);
    // Drawn for exactly one frame, whatever the camera is looking at: that frame is what uploads the canvas
    // to the GPU, and it happens now — while the loading overlay is still up — instead of the first time a
    // car gets near a map edge, where the player would feel it. From the frame after, _updateVisibility
    // decides (see there).
    this._pendingUpload = true;
    this.scene.events.on('prerender', this._updateVisibility, this);
  }

  /**
   * The band draws NOTHING while the view sits inside the map: the painter leaves the map itself
   * transparent — what the player sees in there is the picture — so the image would be a full-screen quad
   * fetching a texture for nothing, every frame. Hidden, Phaser skips it entirely.
   *
   * The one frame it is drawn on purpose is the first one after it is built (see _pendingUpload), and that
   * frame is the whole reason the band can be a few Mpx of texture without anyone noticing: its upload is
   * paid under the overlay. Showing it on demand instead — the first time the camera reaches an edge —
   * would be a stutter in the middle of a lap, once per track load, and it is exactly the kind of hitch a
   * player blames on driving.
   *
   * The slack is what the camera can cover in the couple of frames one of these checks can be stale: the
   * band is shown a little before it is needed, never later.
   */
  _updateVisibility() {
    if (this._pendingUpload) { this._pendingUpload = false; return; } // the frame that uploads the texture
    const image = this.image;
    const view = this.scene.cameras?.main?.worldView;
    if (!image || !view || !this.track) return;
    const slack = INVISIBLE_SLACK;
    const inside = view.x >= slack && view.y >= slack
      && view.right <= this.track.width - slack && view.bottom <= this.track.height - slack;
    image.setVisible(!inside);
  }

  destroy() {
    this.clear();
  }
}

const TAU = Math.PI * 2;

export const TILE = 128;          // procedural tile size, px
export const TERRAIN_TILE = 256;  // terrain images repeat at twice the former size, in world units

// Grass tones. The rest of the palette (asphalt, lines, kerbs) belongs to the track's own look and lives
// in trackPainter, which spreads these in: one definition of the green, whoever paints it.
export const GRASS_PALETTE = {
  grass:      '#4f7614',
  grassLight: '#5e8820',
  grassDark:  '#3d5d13',
};

/** The same green as a number, for the flat wash that stands in for the scenery (see setFlat). */
const GRASS_FILL = Number.parseInt(GRASS_PALETTE.grass.slice(1), 16);

/** Reference-derived transparent crowns, loaded through the host's existing image loader. */
export const VEGETATION_SPRITES = ['tree', 'bush'].flatMap((kind) =>
  Array.from({ length: 5 }, (_, i) => ({ kind, src: `vegetation/${kind}-${i + 1}.png` })));

/**
 * A terrain's texture is always found by the same convention — one folder, the terrain's own name:
 * `textures/terrains/<terrain>.jpg`. The path is relative to the game's assets img root (the host
 * resolves `assets/img/<src>` in game and `/img/<src>` in the editor preview, exactly like materials.js).
 */
const TERRAIN_IMAGE_DIR = 'textures/terrains';
export const terrainImageSrc = (terrain) => `${TERRAIN_IMAGE_DIR}/${terrain}.jpg`;

/** Pixels per world unit a bake is made at, and the ceilings it has to fit in. */
export const PAINT_SCALE = 2;
// What a picture may weigh, in pixels and on a side. This is the ceiling the EDITOR exports at, and it is set
// so that a track comes out at a full pixel per world unit or better — 1:1 is what the hand-drawn pictures the
// game used before were, and it is the number that makes the asphalt read as asphalt. Today's biggest track
// (octobertrack, 7908x4478) lands at 8192x4638 = 38 Mpx, just under the side cap; smaller tracks get more than
// one pixel per unit, up to this budget (crystal_lake: 1.53).
//
// This is the EXPORT's end of the scale, not what the game holds: how much of it a machine keeps is its own
// Graphics setting (clientConfig.QUALITY_LEVELS), which is why the ceiling can afford to be generous — and why
// the softness of a picture is now something to fix at the export, never by lowering the game's default.
const PAINT_MAX_PIXELS = 40e6;   // ~160 MB of RGBA at the very top of the range, plus the GPU copy
const PAINT_MAX_SIDE = 8192;     // what a texture can be, on the hardware that is generous about it

// The band around the map is a BACKDROP — grass, and tree crowns seen from far away — and it is never the
// resolution that matters there: what a track LOOKS like is the picture the editor exports. Painted at the
// picture's ceiling it weighed 16 Mpx (64 MB) on EVERY track, which doubled what a load has to allocate and
// upload, and its upload landed right as the loading overlay came down — the stutter a player sees in the
// first seconds after a switch. This is where it stays a backdrop: ~4 Mpx / 16 MB, never finer than one
// pixel per two world units (see outsideBandScale).
const OUTSIDE_MAX_PIXELS = 4e6;
const OUTSIDE_MAX_SCALE = 0.5;

/**
 * The scale a bake of `track` fits in when `wanted` pixels per world unit are asked for. The ceilings are
 * what a canvas — and the texture it becomes — can carry, so this goes BELOW one for a track bigger than
 * they are: a caller that would rather keep the resolution than the memory floors it at one itself.
 * `maxPixels` is the caller's own budget: the outside band has a much smaller one than the picture.
 */
export function scaleWithinCeilings(track, wanted = PAINT_SCALE, maxPixels = PAINT_MAX_PIXELS) {
  const w = track?.width ?? 0;
  const h = track?.height ?? 0;
  if (!(w > 0) || !(h > 0)) return 1;
  const byPixels = Math.sqrt(maxPixels / (w * h));
  const bySide = PAINT_MAX_SIDE / Math.max(w, h);
  return Math.min(wanted, byPixels, bySide);
}

/**
 * The scale the OUTSIDE band is painted at — its own, smaller ceiling (see OUTSIDE_MAX_PIXELS). Exported so
 * a harness (or a test) asks for the same number the game uses instead of repeating the budget.
 *
 * The game passes its own budget (the Graphics tab's Quality, see TrackRenderer): a backdrop is the first
 * thing that should get cheaper on a weak machine, and it is the only big thing a track load paints.
 */
export function outsideBandScale(bounds, maxPixels = OUTSIDE_MAX_PIXELS, maxScale = OUTSIDE_MAX_SCALE) {
  return scaleWithinCeilings(bounds, maxScale, maxPixels);
}

/** The world rectangle the scenery covers: the map plus the band around it. */
export function outsideBounds(track) {
  return { x: -MARGIN, y: -MARGIN, width: track.width + MARGIN * 2, height: track.height + MARGIN * 2 };
}

// ── deterministic randomness ─────────────────────────────────────────────────────────────────────
// Both the tiles and the scatter are seeded from the track id, so a given track always paints
// identically: the editor never sees a different track from the one the game draws.

export function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// ── the ground tile ──────────────────────────────────────────────────────────────────────────────

export function defaultCreateCanvas(w, h) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  return canvas;
}

/**
 * Keep the repeat distance in world units, independent of the tile's bake resolution: a tile can be made
 * bigger than its world size (see the painter's resoledCanvas) without changing how often it repeats.
 */
const tileWorldSizes = new WeakMap();

/** The world size a tile stands for, for a canvas that was made bigger than it (see the painter's
 *  resoledCanvas): the tile repeats every `size` units however many pixels it was drawn with. */
export function setTileWorldSize(canvas, size) {
  tileWorldSizes.set(canvas, size);
}

export function repeatingPattern(ctx, tile, x = 0, y = 0) {
  const pattern = ctx.createPattern(tile, 'repeat');
  const size = tileWorldSizes.get(tile) ?? tile;
  if (pattern?.setTransform) {
    pattern.setTransform({
      a: size.width / tile.width, b: 0, c: 0, d: size.height / tile.height,
      e: x % size.width, f: y % size.height,
    });
  }
  return pattern;
}

/**
 * Seamless tile seed: `createCanvas` is injectable so this module can also run where there is no
 * DOM (Node harnesses).
 *
 * `image` replaces the procedural blades with a picture when the host has one to give (the base surface
 * IS the `grass` terrain). It is drawn to fill a TERRAIN_TILE-wide tile, preserving larger illustrated
 * details than the procedural fallback. The tile-seed RNG is simply not consumed.
 */
export function makeGrassTile(createCanvas, rng, p, image) {
  const size = image ? TERRAIN_TILE : TILE;
  const canvas = createCanvas(size, size);
  const ctx = canvas.getContext('2d');
  if (image) {
    ctx.drawImage(image, 0, 0, size, size);
    return canvas;
  }
  ctx.fillStyle = p.grass;
  ctx.fillRect(0, 0, TILE, TILE);
  for (let i = 0; i < 2400; i++) {
    // Blades are taller than wide — a flat dot pattern reads as noise, this reads as grass.
    ctx.fillStyle = rng() < 0.5 ? p.grassDark : p.grassLight;
    ctx.globalAlpha = 0.16 + rng() * 0.34;
    ctx.fillRect(rng() * TILE, rng() * TILE, 1 + rng() * 1.4, 1.6 + rng() * 2.6);
  }
  ctx.globalAlpha = 1;
  return canvas;
}

/** The ground: the tile, plus the large-scale variation that stops it reading as a grid. */
export function paintGrass(ctx, track, rng, p, grassPattern, photo, bounds = { x: 0, y: 0, width: track.width, height: track.height }) {
  ctx.fillStyle = grassPattern;
  ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);

  // Large-scale variation, otherwise the repeating tile is obvious on a wide-open map. A photo tile
  // already carries its own patches, but tiling it every TERRAIN_TILE units still reads as a grid:
  // that is broken here with a SLOW drift of soft gradients that fade to nothing. The flat-palette
  // hard-edged discs below are the painted tile's own version and would read as circles over a photo.
  if (photo) {
    const blobs = Math.min(80, Math.round((track.width * track.height) / (1000 * 1000) * 2) + 10);
    for (let i = 0; i < blobs; i++) {
      const x = rng() * track.width;
      const y = rng() * track.height;
      const r = 250 + rng() * 850;
      const tone = rng() < 0.5 ? p.grassDark : p.grassLight;
      // A blob that cannot reach the region being painted is skipped — and skipped AFTER its random
      // numbers are drawn, so the ones that do land are where they have always been.
      if (x + r < bounds.x || x - r > bounds.x + bounds.width) continue;
      if (y + r < bounds.y || y - r > bounds.y + bounds.height) continue;
      const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
      grad.addColorStop(0, `${tone}26`);
      grad.addColorStop(1, `${tone}00`);
      ctx.fillStyle = grad;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    return;
  }
  const area = (track.width * track.height) / (1000 * 1000);
  const patches = Math.min(400, Math.round(area * 6));
  for (let i = 0; i < patches; i++) {
    ctx.fillStyle = rng() < 0.5 ? p.grassLight : p.grassDark;
    ctx.globalAlpha = 0.05 + rng() * 0.12;
    ctx.beginPath();
    ctx.arc(rng() * track.width, rng() * track.height, 150 + rng() * 500, 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ── the trees ────────────────────────────────────────────────────────────────────────────────────

export function paintPlants(ctx, plants, images = {}) {
  ctx.save();
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
  for (const plant of plants) {
    const image = images[plant.src];
    if (!image) continue;
    const scale = plant.size / Math.max(image.width, image.height);
    const w = image.width * scale, h = image.height * scale;
    ctx.fillStyle = 'rgba(15, 29, 5, 0.18)';
    ctx.beginPath();
    ctx.ellipse(plant.x + 5, plant.y + 7, w * 0.46, h * 0.43, 0, 0, TAU); ctx.fill();
    ctx.drawImage(image, plant.x - w / 2, plant.y - h / 2, w, h);
  }
  ctx.restore();
}

/** Stable world cells let either camera request any outside region without re-rolling the scenery. */
export function exteriorVegetationPlacements(track, bounds) {
  if (!(track?.width > 0 && track?.height > 0 && bounds?.width > 0 && bounds?.height > 0)) return [];
  const spacing = 220, plants = [];
  const seed = hashString(String(track.id ?? 'track'));
  const margin = 80; // include crowns and shadows whose centres are just beyond the requested region
  for (let row = Math.floor((bounds.y - margin) / spacing); row <= Math.floor((bounds.y + bounds.height + margin) / spacing); row++) {
    for (let col = Math.floor((bounds.x - margin) / spacing); col <= Math.floor((bounds.x + bounds.width + margin) / spacing); col++) {
      const rng = mulberry32(seed ^ hashString(`outside:${col}:${row}`));
      if (rng() > 0.72) continue;
      const x = (col + 0.25 + rng() * 0.5) * spacing;
      const y = (row + 0.25 + rng() * 0.5) * spacing;
      const kind = rng() < 0.65 ? 'tree' : 'bush';
      const size = kind === 'tree' ? 85 + rng() * 50 : 30 + rng() * 28;
      const variant = Math.floor(rng() * 5), reach = size / 2 + 32;
      // The entire canopy stays outside the playable rectangle, including roads along its edges.
      if (x + reach > 0 && y + reach > 0 && x - reach < track.width && y - reach < track.height) continue;
      plants.push({ x, y, size, kind, src: `vegetation/${kind}-${variant + 1}.png` });
    }
  }
  return plants;
}

/** What the outside needs to be painted: the base grass. The trees come from VEGETATION_SPRITES. */
export function surroundingsImages() {
  return [{ terrain: 'grass', src: terrainImageSrc('grass') }];
}

/**
 * The outside, as a paint function over any region of it (all painting in world coordinates). Used by
 * TrackSurroundings above, which calls it once for the whole band around the map.
 */
export function createTrackSurroundingsPainter(track, opts = {}) {
  const createCanvas = opts.createCanvas ?? defaultCreateCanvas;
  const p = { ...GRASS_PALETTE, ...(opts.palette ?? {}) };
  const seed = hashString(String(track?.id ?? 'track'));
  const grass = makeGrassTile(createCanvas, mulberry32(seed ^ 0x57a551ed), p, opts.terrainImages?.grass);
  const patterns = new WeakMap();
  return (ctx, bounds) => {
    if (!(track?.width > 0 && track?.height > 0 && bounds?.width > 0 && bounds?.height > 0)) return;
    let pattern = patterns.get(ctx);
    if (!pattern) { pattern = repeatingPattern(ctx, grass); patterns.set(ctx, pattern); }
    ctx.save();
    ctx.beginPath();
    ctx.rect(bounds.x, bounds.y, bounds.width, bounds.height); ctx.clip();
    ctx.beginPath();
    ctx.rect(bounds.x, bounds.y, bounds.width, bounds.height);
    ctx.rect(0, 0, track.width, track.height); ctx.clip('evenodd');
    paintGrass(ctx, track, mulberry32(seed ^ 0x85ebca6b), p, pattern, !!opts.terrainImages?.grass, bounds);
    paintPlants(ctx, exteriorVegetationPlacements(track, bounds), opts.images);
    ctx.restore();
  };
}
